<script lang="ts">
  export let type: 'button' | 'submit' | 'reset' = 'button';
  export let variant: 'primary' | 'secondary' | 'text' | 'dark' | 'danger' = 'primary';
  export let disabled: boolean = false;
  export let fullWidth: boolean = false;
  export let loading: boolean = false;
</script>

<button 
  {type} 
  class:primary={variant === 'primary'} 
  class:secondary={variant === 'secondary'} 
  class:text={variant === 'text'} 
  class:dark={variant === 'dark'} 
  class:danger={variant === 'danger'} 
  class:full-width={fullWidth} 
  class:loading={loading} 
  {disabled} 
  {...$$restProps}
  on:click
>
  {#if loading}
    <span class="loader"></span>
  {/if}
  <span class:invisible={loading}>
    <slot>Button</slot>
  </span>
</button>

<style>
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    border-radius: 25rem;
    cursor: pointer;
    font-size: 1rem;
    border: none;
    transition: background-color 0.2s, transform 0.1s;
    position: relative;
    height: 2.5rem !important;
    width: auto !important;
    min-width: auto !important;
    max-width: none !important;
    overflow: visible;
  }
  
  button > span:not(.loader) {
    position: relative;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  /* Ensure button content is always visible */
  button.primary > span:not(.loader),
  button.secondary > span:not(.loader),
  button.text > span:not(.loader) {
    color: inherit !important;
  }
  
  button:active:not(:disabled) {
    transform: translateY(1px);
  }
  
  button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .primary {
    background-color: var(--color-primary, #ffb400) !important;
    color: white !important;
  }
  
  .primary:hover:not(:disabled) {
    background-color: var(--color-primary-hover, #f0a800) !important;
  }
  
  /* Ensure primary buttons never get dark styling accidentally */
  button.primary,
  button.primary.dark {
    background-color: var(--color-primary, #ffb400) !important;
    color: white !important;
  }
  
  button.primary.dark:hover:not(:disabled) {
    background-color: var(--color-primary-hover, #f0a800) !important;
  }
  
  .secondary {
    background-color: #f5f5f5;
    color: #333;
    border: 1px solid #ddd;
  }
  
  .secondary:hover:not(:disabled) {
    background-color: #e0e0e0;
  }
  
  .dark {
    background-color: #121826;
    color: white;
    border: none;
  }
  
  .dark:hover:not(:disabled) {
    background-color: #111827;
  }
  
  .text {
    background-color: transparent;
    color: #ff3e00;
    padding: 0.5rem;
  }
  
  .text:hover:not(:disabled) {
    background-color: rgba(255, 62, 0, 0.1);
  }
  
  .danger {
    background-color: #dc2626;
    color: white;
    border: none;
  }
  
  .danger:hover:not(:disabled) {
    background-color: #b91c1c;
  }
  
  .full-width {
    width: 100%;
  }
  
  .invisible {
    display: none;
  }
  
  .loader {
    width: 16px;
    height: 16px;
    border: 2px solid white;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: none !important;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
    position: absolute;
    left: 50%;
    top: 50%;
    margin-left: -8px;
    margin-top: -8px;
    z-index: 10;
  }
  
  button.loading .loader {
    display: inline-block !important;
  }
  
  /* Ensure loader never shows when not loading */
  button:not(.loading) .loader {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
  }
  
  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style> 